import React, { useEffect, useState } from 'react';

import { Panel, Panels, usePublicaTranslation } from '~/src/dash/publicaUI';
import { isPublisherParent, isPublisherPublica } from '~/src/dash/utils/publishers';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { DashboardsContext } from '~/src/dash/views/dashboards/contexts';
import { EDashboardSection } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardsConfig';
import { DashboardAuctionInsights } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardAuctionInsights';
import { DashboardLive } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardLive';
import { DashboardOverview } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview';
import { DashboardParentOverview } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardParentOverview';
import { DashboardPublicaAnalytics } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardPublicaAnalytics';
import { TopBar } from '~/src/dash/views/topbar/TopBar';
import { resetMomentLocale, updateMomentLocale } from '~/src/locales/momentCfg';

interface IActiveSectionProps {
  activeSection: EDashboardSection;
  isPublica?: boolean;
  isParentPublisher?: boolean;
}

const ActiveSection = ({ activeSection, isParentPublisher, isPublica }: IActiveSectionProps) => {
  switch (activeSection) {
    case EDashboardSection.Live:
      return <DashboardLive />;
    case EDashboardSection.ParentOverview:
      if (isParentPublisher) return <DashboardParentOverview />;
      break;
    case EDashboardSection.AuctionInsights:
      return <DashboardAuctionInsights />;
    case EDashboardSection.PublicaAnalytics:
      if (isPublica) return <DashboardPublicaAnalytics />;
      break;
    default:
      return <DashboardOverview />;
  }
};

export const DashboardHome = () => {
  const { session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { ID } = session.publisher;
  const isParentPublisher = isPublisherParent(ID);
  const isPublica = isPublisherPublica(ID);
  const { Dashboards } = useBaseConfig();
  const { defaultState, sections } = Dashboards.Main;

  const [activeSection, setActiveSection] = useState(
    isPublica ? EDashboardSection.PublicaAnalytics : defaultState.section,
  );

  // move this to top wrapper when ready
  updateMomentLocale(t);

  // this resets it back to Sun-Sat on unmount
  useEffect(() => {
    return () => {
      resetMomentLocale();
    };
  }, []);

  useEffect(() => {
    defaultState.section = activeSection;
  }, [activeSection]);

  return (
    <DashboardsContext.Provider
      value={{ activeSection, setActiveSection, sections, isPublica, isParentPublisher }}
    >
      <Panels max vertical relative minWidthMain>
        <Panel largeHorizontalPadding smallVerticalPadding>
          <TopBar title={t('Dashboards')} enableUINotices />
        </Panel>
        <Panel>
          <ActiveSection
            activeSection={activeSection}
            isPublica={isPublica}
            isParentPublisher={isParentPublisher}
          />
        </Panel>
      </Panels>
    </DashboardsContext.Provider>
  );
};
